import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { STATUS_FAILED } from '@/utils/constants';
import useStore from '@/store/useStore';
import Button from '@/components/homepage/Button';
import Input from '@/components/Form/Input';
import beTwMerge from '@/utils/beTwMerge';
import { trackEventWithObject } from '@/utils/gaDataPush';
import useGoogleLogin from '@/hooks/useGoogleLogin';
import { MANUAL_SIGN_IN, BE_LOG_IN } from '@/consts/GA';
import { getCsrf } from '@/services/auth.service';
import FontIcon from '../FontIcon';
import GoogleIcon from '../GoogleIcon';

export default function User() {
  const [open, setOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const login = useStore(state => state.login);
  const logout = useStore(state => state.logout);
  const getHeaderProducts = useStore(state => state.getHeaderProducts);
  const { googleLoginUrl, pathname, setLocaleIntoSession } = useGoogleLogin();
  const formRef = useRef<any>(null);
  const inputRef = useRef<any>(null);

  const {
    register,
    getValues,
    setError,
    trigger,
    reset,
    formState: { errors },
  } = useForm();

  const { isAuthenticated, isPosAdyen, isSalesRep, notInternal, userName } = useStore(state => state.user);
  const canSeeCompleteOrderBalance = isPosAdyen && isSalesRep;
  const canSeeWalkInWishList = !notInternal;

  async function validateForm() {
    trackEventWithObject({
      event: 'header',
      general_event_category: 'Sign In',
      general_event_section: 'Sign In',
      general_event_details: 'Undefined',
    });

    const isFormValid = await trigger();

    if (isFormValid) {
      setIsSending(true);
      login(getValues() as any)
        .then(({ status, errors: err }) => {
          if (status === STATUS_FAILED) {
            const errorMessage: string = err.username.reduce((prev, curr) => prev + curr, '');
            setError('password', { type: 'custom', message: errorMessage });
          } else {
            (window as any).dataLayer.push({
              event: BE_LOG_IN,
              form_location: 'header',
              general_event_details: MANUAL_SIGN_IN,
            });
            getHeaderProducts();
            setOpen(false);
            setIsFocused(false);
            reset();
          }
        })
        .finally(() => {
          setIsSending(false);
        });
    }
  }

  function keyDownHandler(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      validateForm();
    }
  }

  function signOut() {
    trackEventWithObject({
      event: 'header',
      general_event_category: 'Sign In',
      general_event_section: 'Sign Out',
      general_event_details: 'Undefined',
    });
    setOpen(false);
    logout();
  }
  const onGoogleLoginClick = async () => {
    try {
      const { data } = await getCsrf();
      if (!data?.token) return;
      inputRef.current.value = data?.token;
      formRef.current.submit();
    } catch (error) {
      return;
    }
    setLocaleIntoSession();
    (window as any).dataLayer.push({
      event: 'social_sign_in_cta',
      general_event_details: 'Google',
      form_location: 'header',
    });
    trackEventWithObject({
      event: 'header',
      general_event_category: 'Sign In',
      general_event_section: 'Sign In with Google',
      general_event_details: 'Undefined',
    });
  };

  return (
    <div
      className={beTwMerge('dropdown-inner', (open || isFocused) && 'open')}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <a
        aria-label="Sign In"
        href="/accounts/login/"
        className="navitem js-activated"
        onClick={() => {
          trackEventWithObject({
            event: 'header',
            general_event_category: 'Sign In',
            general_event_section: 'Main Icon',
            general_event_details: 'Undefined',
          });
        }}
        data-event1-category="Header"
        data-event1-action="Click"
        data-event1-label="Login Dropdown"
      >
        <span className="tw-pr-[0.3em] tw-right-[100%]" id="user-name">
          {isAuthenticated ? `Hi, ${userName}` : ''}
        </span>
        <FontIcon name="user" />
      </a>

      <div className="dropdown-menu dropdown-menu-login" id="user_login">
        <div className="dropdown-menu-inner !tw-p-[20px] !tw-border !tw-shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] !tw-border-solid !tw-border-[#d9d9d9] tw-mt-3">
          {isAuthenticated ? (
            <>
              <ul className="list-unstyled lh-28 tw-mb-6">
                <li>
                  <a
                    href="/accounts/"
                    onClick={() => {
                      trackEventWithObject({
                        event: 'header',
                        general_event_category: 'Sign In',
                        general_event_section: 'My Account',
                        general_event_details: 'Undefined',
                      });
                    }}
                  >
                    My Account
                  </a>
                </li>
                <li>
                  <a
                    href="/accounts/order-history/"
                    onClick={() => {
                      trackEventWithObject({
                        event: 'header',
                        general_event_category: 'Sign In',
                        general_event_section: 'Order History',
                        general_event_details: 'Undefined',
                      });
                    }}
                  >
                    Order History
                  </a>
                </li>
                <li>
                  <a
                    href="/shop/wishlist/"
                    onClick={() => {
                      trackEventWithObject({
                        event: 'header',
                        general_event_category: 'Sign In',
                        general_event_section: 'Wish List',
                        general_event_details: 'Undefined',
                      });
                    }}
                  >
                    Wish List
                  </a>
                </li>
                {canSeeCompleteOrderBalance && (
                  <li>
                    <a href="/accounts/complete-order-balance/" id="user_internal">
                      Complete Order Balance
                    </a>
                  </li>
                )}
                {canSeeWalkInWishList && (
                  <li>
                    <a href="/repshare-form/">Walk-In Wish List</a>
                  </li>
                )}
              </ul>
              <button onClick={signOut} type="button" className="btn btn-success btn-block">
                Sign Out
              </button>
            </>
          ) : (
            <div className="site-header-login tw-text-be-dark-gray">
              <div className="tw-w-full">
                <p className="tw-leading-[15.4px] tw-tracking-[0.35px] tw-text-center tw-mt-[5px] tw-mb-[15px] tw-font-avenirmedium">
                  YOUR ACCOUNT
                </p>
                <form action={googleLoginUrl} method="post" onKeyDown={keyDownHandler} ref={formRef}>
                  <div className="tw-col-span-full tw-flex tw-justify-center tw-font-avenirmedium tw-mb-[30px]">
                    <input type="hidden" name="next" value={pathname} />
                    <input type="hidden" name="3rd_auth_button_location" value="header" />
                    <input type="hidden" name="csrfmiddlewaretoken" value="" ref={inputRef} />
                    <button
                      onClick={onGoogleLoginClick}
                      type="button"
                      className="btn btn-block tw-border-[#D9D9D9] tw-bg-white tw-h-[48px] tw-flex tw-gap-[10px] tw-justify-center tw-items-center tw-px-3 tw-py-0 hover:tw-bg-[#ededed] !tw-shadow-none"
                    >
                      <GoogleIcon />
                      <span className="tw-pt-[3px] tw-tracking-normal tw-text-be-dark-gray tw-text-[14px] tw-font-avenirmedium">
                        Sign in with Google
                      </span>
                    </button>
                  </div>
                  <div className="tw-grid md:tw-grid-cols-2 tw-gap-y-[15px] tw-text-[16px] tw-font-book">
                    <Input
                      fullWidth
                      registerObject={{
                        required: true,
                        noRequiredSymbol: true,
                        validate: {
                          isValid: v =>
                            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/.test(v) || 'Invalid Email Address.',
                        },
                      }}
                      register={register as any}
                      label="Email Address"
                      inputClassName=""
                      labelClassName=""
                      labelType="inline"
                      name="username"
                      id="username"
                      error={errors['username'] as any}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => setIsFocused(false)}
                      errorColor="true"
                    />
                    <Input
                      fullWidth
                      registerObject={{ required: true, noRequiredSymbol: true }}
                      register={register as any}
                      label="Password"
                      inputClassName=""
                      labelClassName=""
                      labelType="inline"
                      name="password"
                      id="password"
                      type="password"
                      error={errors['password'] as any}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => setIsFocused(false)}
                      autoComplete="current-password"
                      errorColor="true"
                    />
                  </div>

                  <div className="tw-col-span-full tw-flex tw-justify-center tw-mt-[15px] tw-mb-[30px]">
                    <Button
                      className="tw-text-[14px] !tw-block tw-font-book !tw-text-be-dark-gray hover:!tw-text-be-turquoise-green"
                      label="Forgot Your Password?"
                      color="dark-green"
                      type="link"
                      href="/accounts/password_reset/"
                      onClick={() => {
                        trackEventWithObject({
                          event: 'header',
                          general_event_category: 'Sign In',
                          general_event_section: 'Forgot Your Password?',
                          general_event_details: 'Undefined',
                        });
                      }}
                    />
                  </div>
                  <div className="tw-col-span-full tw-flex tw-justify-center">
                    <Button
                      className="!tw-w-full tw-text-[14px] tw-text-white tw-font-avenirmedium tw-tracking-wider tw-h-[48px]"
                      label="SIGN IN"
                      color="dark-green"
                      type="primary"
                      onClick={validateForm}
                      disabled={isSending}
                    />
                  </div>
                  <div className="tw-col-span-full tw-flex tw-justify-center tw-mt-3.5">
                    <Button
                      className="!tw-w-full tw-text-[14px] tw-font-avenirmedium hover:!tw-text-white tw-tracking-wider"
                      label="CREATE AN ACCOUNT"
                      type="secondary"
                      href="/accounts/login/"
                      onClick={() => {
                        trackEventWithObject({
                          event: 'header',
                          general_event_category: 'Sign In',
                          general_event_section: 'Create An Account',
                          general_event_details: 'Undefined',
                        });
                      }}
                    />
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
